/**
 * @module experience/device-assessment
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */
/** */
/* tslint:disable:no-any */ // We need to be able to allow developers send any payload the want
// Core libraries
import { Injectable } from '@angular/core';
import { Wove } from 'aspect.js/';
// services
import { ApiGatewayService } from '@core/api/services/api-gateway-service';
import { ObjectService } from '@support/state-object-hash-utilities/services/object.service';
import { StateStoreUtilsService } from '@support/state-object-hash-utilities/utils/state-store-utils.service';
import { CartSupportService } from '@support/cart-support/services/cart-support.service';
import { CommonUtilService } from '@support/shared/services/common-util.service';

// Interfaces
import { APIError } from '@support/api/common/error.interface';
import {
  CartResponse,
  CartItem,
  Cart,
  API2127Payload,
  CartLine,
} from '@support/api/proxies/responses/api-cart-response.interface';
import { Product } from '@support/api/common/product.interface';

// Models
import { RemorseExchangeCartPayloadModel } from '@experience/shared-experience/models/remorse-exchange-update-cart.model';
import { CartRequestPayloadModel } from '@experience/device-assessment/model/assess-item.model';

// constants
import {
  DEVICE_ASSESSSMENT_CONSTANT,
  CART_METHOD,
} from '@experience/device-assessment/device-assessment.constants';
import { API_MAP } from '@core/api/proxies/common-api-endpoints';
import {
  AssessmentItem,
  OrderLine,
  ProductsIdAssessmentItemMap,
  SelectedItems,
  ReturnReason,
} from '@experience/order-summary/interfaces/order-summary-new.interface';
import { Product as Device } from '@support/api/proxies/responses/api-2814-new.interface';
import { SelectedAssessmentQuestions } from '@experience/device-assessment/interfaces/device-assessment.interface';
import { ReturnSNValidation } from '@support/api/proxies/responses/api-2152.interface';
import { SalesSNValidation } from '@support/api/proxies/responses/api-2151.interface';
import { throwError as observableThrowError, Observable } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { HttpResponse, HttpHeaders } from '@angular/common/http';

@Wove({ moduleName: DEVICE_ASSESSSMENT_CONSTANT.DEVICE_ASSESSMENT_MODULE_NAME })
@Injectable()
export class RemorseService {
  constructor(
    private objectService: ObjectService,
    private stateStoreUtilsService: StateStoreUtilsService,
    private cartSupportService: CartSupportService,
    private apiGatewayService: ApiGatewayService,
    private commonUtilService: CommonUtilService
  ) {}

  /**
   * Creates form for the dealer sku code
   * Flow - Remorse
   * Usage - To call the remorse API to add the scanned SKU to the cart
   * API - API 2134(Device), API 2137(Accessories)
   * @params sku: sku scanned
   * @params product: Product associated with sku scanned
   * @return CartResponse.
   */
  public postRemorseItemToCart(
    product: Product,
    sku: string,
    quantity?: number
  ): Observable<CartResponse> {
    let requestPayload;
    requestPayload = new CartRequestPayloadModel(
      CART_METHOD.POST,
      null,
      null,
      null,
      null,
      this.stateStoreUtilsService,
      this.objectService,
      null,
      null,
      null,
      product
    );
    const url = requestPayload.apiUrl;
    const pathParams = requestPayload.apiPathParams;
    requestPayload = requestPayload.requestPayloadRemorseExchange;

    if (quantity) {
      requestPayload.quantity = quantity;
    }
    return this.apiGatewayService.postRequest(url, requestPayload, null, null, pathParams).pipe(
      map((response: HttpResponse<Object>) => {
        return response.body;
      }),
      catchError((error: APIError) => {
        return observableThrowError(error);
      })
    );
  }

  /**
   * Gets products details based on SKU API:2127
   * @param sku: string
   * @param billingType: string
   * @return Observable<Product[]>
   */
  public getProductDetailsFromSKu(payload: API2127Payload): Observable<Product[]> {
    return this.apiGatewayService
      .postRequest(API_MAP.API_2127_SimDetails, payload, null, null)
      .pipe(
        map((response: HttpResponse<Object>) => {
          if (response) {
            return this.objectService.get(response.body, 'products');
          }
        }),
        catchError((error: APIError[]) => {
          return observableThrowError(error);
        })
      );
  }

  /**
   * update the AssessItemProducstMap to replacement Ids
   * @param {string} returningItemId
   * @param {Cart} cart
   * @param enteredSku string
   */
  public updateAssessmentMappingWithCartItemId(returningItemId?: string, cart?: Cart): void {
    const assesmentMap = this.stateStoreUtilsService.getAssessmentProductsMap();
    let replacementItemId: string;
    const lineId = this.objectService.get(assesmentMap, `${returningItemId}.lineId`);
    if (lineId) {
      const lineInFocus = cart.lines.find((line: CartLine) => line.id === lineId);
      replacementItemId = this.objectService.get(lineInFocus, `replacementItem.id`);
    } else {
      const replacementItems = this.objectService.get(
        this.stateStoreUtilsService.getCart(),
        'replacementItem'
      );
      const returningtItems = this.objectService.get(
        this.stateStoreUtilsService.getCart(),
        'returningItem'
      );
      let nonLineReplacementItemsCount: number = -1;
      let replacementId: string;
      if (returningtItems.length >= replacementItems.length) {
        for (const item of replacementItems) {
          if (!this.objectService.get(item, `lineId`)) {
            nonLineReplacementItemsCount++;
            replacementId = this.objectService.get(item, `id`);
          }
        }
        returningItemId = this.objectService.get(
          returningtItems[nonLineReplacementItemsCount],
          `id`
        );
        for (const assesmentItemId of Object.keys(assesmentMap)) {
          const isItemPresentInMap = this.objectService.get(
            assesmentMap,
            `${assesmentItemId}.cartItemIdTo`
          );
          if (
            !isItemPresentInMap &&
            !this.objectService.get(assesmentMap, `${assesmentItemId}.lineId`)
          ) {
            nonLineReplacementItemsCount--;
            if (nonLineReplacementItemsCount < 0) {
              replacementItemId = replacementId;
              break;
            }
          }
        }
      }
    }

    this.updateAssessmentProductsMapping(returningItemId, null, replacementItemId);
  }

  /**
   * Handles addition of replacemant Item on scanning on cart review
   * @param {string} sku
   * @param {Product} product
   * @returns {Observable<Cart>}
   */
  public handleAddToCart(sku: string, product: Product): Observable<Cart> {
    let isLineItem = false;
    const cart = this.stateStoreUtilsService.getCart();
    const lines = this.objectService.get(cart, 'lines');
    const productType = this.commonUtilService.toUpperCase(
      this.objectService.get(product, 'productType')
    );
    const offerId = this.objectService.get(product, 'offerId');
    if (
      productType === DEVICE_ASSESSSMENT_CONSTANT.DEVICE &&
      this.objectService.get(lines, 'length')
    ) {
      for (const line of lines) {
        const returningItem = this.objectService.get(line, 'returningItem');
        const lineId = this.objectService.get(line, 'id');
        const replacementItemSku = this.objectService.get(line, 'replacementItem.sku');
        if (!replacementItemSku && returningItem) {
          const returningItemId = this.objectService.get(returningItem, 'id', '');
          isLineItem = true;
          this.stateStoreUtilsService.setRemorseExchangeCheckout(true);
          return this.addDeviceToLine(sku, offerId, returningItemId, lineId);
        }
      }
    }

    if (!isLineItem) {
      return this.handleScannedNonLineItem(sku, offerId);
    }
  }
  /**
   * API call 2151 with sku and serial number
   * @param {string} devicesku
   * @param {string} serialNumber
   * @returns {Observable<SalesSNValidation>}
   */
  public getSalesSNValidation(
    deviceSku: string,
    serialNumber: string
  ): Observable<SalesSNValidation> {
    const queryParams = {
      serialNumber: serialNumber,
      sku: deviceSku,
    };
    return this.apiGatewayService
      .getRequest(API_MAP.API_2151_getSalesSNValidation, null, queryParams, null)
      .pipe(
        map((response: HttpResponse<Object>) => {
          return response.body;
        }),
        catchError((error: APIError[]) => {
          return observableThrowError(error);
        })
      );
  }
  /**
   * API call 2152 with devicesku and serial number
   * @param {string} devicesku
   * @param {Product} serialNumber
   * @returns {Observable<ReturnSNValidation>}
   */
  public getReturnSNValidation(
    devicesku: string,
    serialNumber: string
  ): Observable<ReturnSNValidation> {
    const headers = new HttpHeaders();
    const queryParams = {
      serialNumber: serialNumber,
      sku: devicesku,
    };
    return this.apiGatewayService
      .getRequest(API_MAP.API_2152_getReturnSNValidation, headers, queryParams, null)
      .pipe(
        map((response: HttpResponse<Object>) => {
          return response.body;
        }),
        catchError((error: APIError) => {
          return observableThrowError(error);
        })
      );
  }
  /**
   * Handles the scanning of a Non Lined Item
   * @param sku : sku of scanned Device
   * @param product : scanned product
   * @param returningItem : returning Items array
   * @return void
   */
  private handleScannedNonLineItem(sku: string, offerId: string): Observable<Cart> {
    let returningItemId = '';
    const returningItems = this.objectService.get(
      this.stateStoreUtilsService.getCart(),
      'returningItem'
    );
    const replacementItems = this.objectService.get(
      this.stateStoreUtilsService.getCart(),
      'replacementItem'
    );
    if (returningItems && replacementItems && replacementItems.length < returningItems.length) {
      let nonLineReplacementItemsCount: number = -1;
      for (const item of replacementItems) {
        if (!this.objectService.get(item, `lineId`)) {
          nonLineReplacementItemsCount++;
        }
      }
      for (const item of returningItems) {
        const isItemPresentInMap = this.checkReplacementItemMapping(item);
        if (!isItemPresentInMap) {
          nonLineReplacementItemsCount--;
          returningItemId = this.objectService.get(item, 'id', '');
          if (nonLineReplacementItemsCount < 0) {
            break;
          }
        }
      }
    }

    return this.addNonLineItem(sku, offerId, returningItemId);
  }

  /**
   * Adds Device to Line as Replacement Item
   * @param sku : sku of scanned Device
   * @param product : scanned product
   * @param returningItem : returning Item
   * @return void
   */
  private addDeviceToLine(
    sku: string,
    offerId: string,
    returningItemId?: string,
    lineId?: string
  ): Observable<Cart> {
    const requestPayload = new RemorseExchangeCartPayloadModel(sku, offerId, returningItemId);

    return this.cartSupportService.updateLineDevice(
      null,
      null,
      null,
      true,
      requestPayload.RequestPayloadRemorseExchange,
      lineId
    );
  }

  /**
   * Adds non lined Item to Cart as Replacement/ Purchase Item
   * @param sku : sku of scanned Device
   * @param product : scanned product
   * @param returningItem : returning Item
   * @return void
   */
  private addNonLineItem(sku: string, offerId: string, returningItemId?: string): Observable<Cart> {
    const requestPayload = new RemorseExchangeCartPayloadModel(sku, offerId, returningItemId);

    return this.cartSupportService.postDeviceAsNonLineItem(
      null,
      null,
      true,
      requestPayload.RequestPayloadRemorseExchange
    );
  }

  /**
   * Checks whether the scanned sku is already mapped to any returning Item
   * @param Item : returning Item
   * @return boolean
   */
  private checkReplacementItemMapping(item: CartItem): boolean {
    const assesmentMap = this.stateStoreUtilsService.getAssessmentProductsMap();
    const itemId = this.objectService.get(item, 'id');
    const cartItemIdTo = this.objectService.get(assesmentMap, `${itemId}.cartItemIdTo`) || '';
    return this.commonUtilService.isNotEmpty(cartItemIdTo);
  }

  /**
   * Method updates AssessmentItem with cartItemID by passing
   * @param {string} productId
   * @param {string} cartItemIdFrom
   * @param {string} cartItemIdTo
   * @param {string} reason
   */
  public updateAssessmentProductsMapping(
    productId: string,
    cartItemIdFrom?: string,
    cartItemIdTo?: string,
    returnReason?: ReturnReason
  ): void {
    const productsIdAssessmentItemMap: ProductsIdAssessmentItemMap =
      this.stateStoreUtilsService.getAssessmentProductsMap();
    if (this.objectService.get(productsIdAssessmentItemMap, `${productId}`)) {
      if (cartItemIdFrom) {
        productsIdAssessmentItemMap[productId].cartItemIdFrom = cartItemIdFrom;
      }
      productsIdAssessmentItemMap[productId].cartItemIdTo = cartItemIdTo;
      productsIdAssessmentItemMap[productId].reason = this.objectService.get(
        returnReason,
        `reason`
      );
      productsIdAssessmentItemMap[productId].isItemSellable = this.objectService.get(
        returnReason,
        `isItemSellable`
      );
      productsIdAssessmentItemMap[productId].selectUnsellableReason = this.objectService.get(
        returnReason,
        `selectUnsellableReason`
      );
      productsIdAssessmentItemMap[productId].licensePlate = this.objectService.get(
        returnReason,
        `licensePlate`
      );
      this.stateStoreUtilsService.setAssessmentProductsMap(productsIdAssessmentItemMap);
    }
  }

  /**
   * Method updates AssessmentItem with deviceAssessment question and answers
   * @param {string} productId
   * @param {selectedAnswer} selectedAnswer
   */
  public updateAssessmentProductsMapWithAnswers(
    productId: string,
    selectedAnswer?: SelectedAssessmentQuestions[]
  ): void {
    const productsIdAssessmentItemMap: ProductsIdAssessmentItemMap =
      this.stateStoreUtilsService.getAssessmentProductsMap();
    if (
      this.objectService.get(productsIdAssessmentItemMap, `${productId}`) &&
      this.objectService.get(selectedAnswer, 'length')
    ) {
      productsIdAssessmentItemMap[productId].selectedAnswer = selectedAnswer;
      this.stateStoreUtilsService.setAssessmentProductsMap(productsIdAssessmentItemMap);
    }
  }
  /**
   * Method to get AssessmentItem by passing product Id
   * @param {string} productId
   * @returns {AssessmentItem}
   */
  public getAssessmentProductsMapping(productId: string): AssessmentItem {
    const productsIdAssessmentItemMap: ProductsIdAssessmentItemMap =
      this.stateStoreUtilsService.getAssessmentProductsMap();
    return productId && productsIdAssessmentItemMap ? productsIdAssessmentItemMap[productId] : null;
  }
  /**
   * Creates map to identify Assessing item in context
   * @param {SelectedItems} selectedItems
   */
  public setAssessmentProductsMapping(selectedItems: SelectedItems): void {
    if (
      !this.commonUtilService.isNotEmpty(this.stateStoreUtilsService.getAssessmentProductsMap())
    ) {
      const productsIdAssessmentItemMap: ProductsIdAssessmentItemMap = {};
      if (this.commonUtilService.isNotEmpty(selectedItems.line)) {
        selectedItems.line.forEach((item: OrderLine) => {
          productsIdAssessmentItemMap[item.devices.id] = {
            assessmentType: DEVICE_ASSESSSMENT_CONSTANT.LINE_DEVICE,
            lineId: item.id,
          };
        });
      }
      if (this.commonUtilService.isNotEmpty(selectedItems.devices)) {
        selectedItems.devices.forEach((item: Device) => {
          productsIdAssessmentItemMap[item.id] = {
            assessmentType: DEVICE_ASSESSSMENT_CONSTANT.DEVICE,
          };
        });
      }
      if (this.commonUtilService.isNotEmpty(selectedItems.accessory)) {
        selectedItems.accessory.forEach((item: Device) => {
          productsIdAssessmentItemMap[item.id] = {
            assessmentType: DEVICE_ASSESSSMENT_CONSTANT.ACCESSORY,
          };
        });
      }
      if (this.commonUtilService.isNotEmpty(selectedItems.ePinData)) {
        selectedItems.ePinData.forEach((item: Device) => {
          productsIdAssessmentItemMap[item.id] = {
            assessmentType: DEVICE_ASSESSSMENT_CONSTANT.EPIN,
          };
        });
      }
      if (this.commonUtilService.isNotEmpty(selectedItems.directToAccountData)) {
        selectedItems.directToAccountData.forEach((item: Device) => {
          productsIdAssessmentItemMap[item.id] = {
            assessmentType: DEVICE_ASSESSSMENT_CONSTANT.D2A,
          };
        });
      }
      this.stateStoreUtilsService.setAssessmentProductsMap(productsIdAssessmentItemMap);
    }
  }
}
